import axios from 'axios';
import { environment } from '@/environments/environment';

// In-memory cache for offline fallback
const cache = new Map();
const CACHE_MAX_AGE = 30 * 60 * 1000; // 30 minutes

const api = axios.create({
  baseURL: environment.apiUrl,
  timeout: 8000, // 8s timeout to detect offline fast
});

// Helper: Check if request is cacheable GET
const isCacheable = (config) => {
  return config.method === 'get' || config.method === 'GET';
};

// Helper: Get cache key
const getCacheKey = (config) => {
  return `${config.url}${JSON.stringify(config.params || {})}`;
};

// Request interceptor - Add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token && !config.url.includes('/auth/login/professional')) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Mark if this request is retry (to prevent infinite loops)
    config._isRetry = config._isRetry || false;
    config._startTime = Date.now();
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - Handle everything transparently
api.interceptors.response.use(
  (response) => {
    // Cache successful GET requests
    if (isCacheable(response.config)) {
      const key = getCacheKey(response.config);
      cache.set(key, {
        data: response.data,
        timestamp: Date.now(),
        etag: response.headers.etag
      });
    }
    
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // If no config, can't retry
    if (!originalRequest) return Promise.reject(error);
    
    // Check if it's a network error (server offline)
    const isNetworkError = !error.response || 
                           error.code === 'ECONNABORTED' ||
                           error.message.includes('Network Error') ||
                           error.message.includes('timeout');
    
    // =====================
    // GET Requests: Silent fallback to cache
    // =====================
    if (isCacheable(originalRequest) && isNetworkError && !originalRequest._isRetry) {
      const key = getCacheKey(originalRequest);
      const cached = cache.get(key);
      
      if (cached && (Date.now() - cached.timestamp) < CACHE_MAX_AGE) {
        console.log(`📦 Serving cached data for ${originalRequest.url}`);
        
        // Return cached response transparently
        // Components never know there was an error
        return Promise.resolve({
          data: cached.data,
          status: 200,
          statusText: 'OK (cached)',
          headers: {},
          config: originalRequest,
          _fromCache: true // Internal flag, components don't use this
        });
      }
    }
    
    // =====================
    // POST/PUT/DELETE: Show friendly error (catch block handles it)
    // =====================
    if (isNetworkError) {
      // Create enhanced error that catch blocks can detect
      error.isOffline = true;
      error.userMessage = 'Server is temporarily unavailable. Please try again in a moment.';
      
      // Auto-retry logic for mutations (optional, 1 retry)
      if (!isCacheable(originalRequest) && !originalRequest._isRetry) {
        originalRequest._isRetry = true;
        console.log(`🔄 Retrying ${originalRequest.method} ${originalRequest.url}...`);
        
        // Wait 2 seconds then retry once
        await new Promise(resolve => setTimeout(resolve, 2000));
        return api(originalRequest);
      }
    }
    
    // =====================
    // 401 Handling (your existing logic)
    // =====================
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
      return Promise.reject(error);
    }
    
    return Promise.reject(error);
  }
);

export default api;